import { useCallback, useState } from 'react'

import { DateHelper } from '@loadsmart/loadsmart-ui'

import { RequestStatus } from 'core/domain/Request'

import {
  type ShipmentChargeStatus,
  type ShipmentCharge,
  updateShipmentCharge,
} from '../../domain/ShipmentCharges'
import { getShipmentCharges } from '../../use-cases/getShipmentCharges'

export type ShipmentChargesColumnKey = 'type' | 'createdBy' | 'createdAt' | 'status' | 'total'

enum SortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type SortDescriptor = {
  column: ShipmentChargesColumnKey
  direction: SortDirection
}

const sortWeightByStatus: Record<ShipmentChargeStatus, number> = {
  pending: 1,
  approved: 2,
  rejected: 3,
  canceled: 4,
}

function getChargeValueToSort(
  charge: ShipmentCharge,
  column: ShipmentChargesColumnKey
): string | number {
  switch (column) {
    case 'type':
      return charge.type?.name ?? ''
    case 'createdBy':
      return charge.createdBy ?? ''
    case 'createdAt':
      return charge.createdAt ? DateHelper(charge.createdAt).getTime() : 0
    case 'status':
      return sortWeightByStatus[charge.status.value]
    case 'total':
    default:
      return charge.total
  }
}

function sortCharges(
  chargeA: ShipmentCharge,
  chargeB: ShipmentCharge,
  sortDescriptor: SortDescriptor
) {
  const valueA = getChargeValueToSort(chargeA, sortDescriptor.column)
  const valueB = getChargeValueToSort(chargeB, sortDescriptor.column)

  if (valueA > valueB) {
    return sortDescriptor.direction === SortDirection.DESC ? -1 : 1
  }
  if (valueA < valueB) {
    return sortDescriptor.direction === SortDirection.DESC ? 1 : -1
  }
  return 0
}

type ShipmentChargesState = {
  shipmentCharges: Array<ShipmentCharge>
  shipmentChargesStatus: RequestStatus
}

const initialShipmentChargesState = {
  shipmentCharges: [],
  shipmentChargesStatus: RequestStatus.Initial,
}

export function useShipmentCharges() {
  const [{ shipmentCharges, shipmentChargesStatus }, setShipmentChargesState] =
    useState<ShipmentChargesState>(initialShipmentChargesState)
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>()

  const fetchShipmentCharges = useCallback(
    async (shipmentUUID: string, { signal }: AbortController) => {
      try {
        setShipmentChargesState(previous => ({
          ...previous,
          shipmentChargesStatus: RequestStatus.Pending,
        }))

        const response = await getShipmentCharges(shipmentUUID, { signal })

        setShipmentChargesState(previous => ({
          ...previous,
          shipmentCharges: response,
          shipmentChargesStatus: RequestStatus.Done,
        }))
      } catch (error) {
        if (!signal.aborted) {
          setShipmentChargesState(previous => ({
            ...previous,
            shipmentCharges: [],
            shipmentChargesStatus: RequestStatus.Error,
          }))
        }
      }
    },
    []
  )

  const refetchShipmentCharges = useCallback(async (shipmentUUID: string) => {
    try {
      const response = await getShipmentCharges(shipmentUUID)

      setShipmentChargesState(previous => ({
        ...previous,
        shipmentCharges: response,
        shipmentChargesStatus: RequestStatus.Done,
      }))
    } catch (error) {
      setShipmentChargesState(previous => ({
        ...previous,
        shipmentCharges: [],
        shipmentChargesStatus: RequestStatus.Error,
      }))
    }
  }, [])

  const onSort = useCallback(
    (column: ShipmentChargesColumnKey) => {
      let newSortDescriptor: SortDescriptor = {
        direction: SortDirection.ASC,
        column: column,
      }

      if (sortDescriptor?.column === column) {
        newSortDescriptor.direction =
          sortDescriptor.direction === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC
      }
      setShipmentChargesState(previous => ({
        ...previous,
        shipmentCharges: [...previous.shipmentCharges].sort((chargeA, chargeB) =>
          sortCharges(chargeA, chargeB, newSortDescriptor)
        ),
      }))
      setSortDescriptor(newSortDescriptor)
    },
    [sortDescriptor?.column, sortDescriptor?.direction]
  )

  const addChargeToList = useCallback((createdCharge: ShipmentCharge) => {
    setShipmentChargesState(previous => ({
      ...previous,
      shipmentCharges: [...previous.shipmentCharges, createdCharge],
    }))
  }, [])

  const updateCharge = useCallback((newCharge?: ShipmentCharge) => {
    if (newCharge) {
      setShipmentChargesState(previous => ({
        ...previous,
        shipmentCharges: previous.shipmentCharges.map(charge =>
          updateShipmentCharge(charge, newCharge)
        ),
      }))
    }
  }, [])

  return {
    shipmentCharges,
    shipmentChargesStatus,
    sortDescriptor,
    onSort,
    fetchShipmentCharges,
    refetchShipmentCharges,
    addChargeToList,
    updateCharge,
  }
}
